import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { Section } from 'src/components/widgets/section';
import { T } from 'src/shared/helpers';
import styled, { keyframes } from 'styled-components';
import { Badge } from '../../widgets/badge';
import { PrimaryButton } from '../../widgets/buttons';
import { EmptyList } from '../../widgets/emptyList';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { Row, Table, TableContentLoader, TableHeading } from '../../widgets/table';
import { libraryImportTasksQuery, restartLibraryImportTaskMutation } from './libraryImportTaskQueries';
import { libraryImportTaskQueriesRestartMutation } from 'src/components/pages/images/__generated__/libraryImportTaskQueriesRestartMutation.graphql';
import { libraryImportTaskQueriesListQuery } from 'src/components/pages/images/__generated__/libraryImportTaskQueriesListQuery.graphql';

// Numeric state matching the ImageService schema: 0=Waiting, 1=Processing, 2=Completed, 3=Error
const STATE_WAITING = 0;
const STATE_PROCESSING = 1;
const STATE_COMPLETED = 2;
const STATE_ERROR = 3;

// Numeric target matching the ImageService schema: 0=ImageGallery, 1=Background. A seller can have one import task per
// target, so every consumer must filter tasks by the target of the page/gallery it is showing (the image gallery must
// ignore a background import and vice versa).
export const IMPORT_TARGET_IMAGE_GALLERY = 0;
export const IMPORT_TARGET_BACKGROUND = 1;

const POLLING_INTERVAL_MS = 4000;

const TableWrapper = styled.div`
    padding: 0 30px 30px 30px;
    background-color: white;
`;

const CellContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const ProgressBarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const ProgressBar = styled.div`
    flex: 1;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
    min-width: 60px;
`;

const ProgressFill = styled.div<{ $progress: number; $state: number }>`
    height: 100%;
    width: ${(props) => props.$progress}%;
    border-radius: 3px;
    transition: width 0.3s ease;

    ${(props) => {
        switch (props.$state) {
            case STATE_COMPLETED:
                return 'background: #4caf50;';
            case STATE_ERROR:
                return 'background: #f44336;';
            default:
                return 'background: #2196f3;';
        }
    }}
`;

const indeterminate = keyframes`
    0% { left: -40%; }
    100% { left: 100%; }
`;

const IndeterminateFill = styled.div`
    position: relative;
    height: 100%;
    width: 100%;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -40%;
        height: 100%;
        width: 40%;
        border-radius: 3px;
        background: #90caf9;
        animation: ${indeterminate} 1.2s ease-in-out infinite;
    }
`;

const ProgressText = styled.span`
    font-size: 12px;
    color: #666;
    min-width: 40px;
    text-align: right;
`;

const ActionButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    width: 100%;
`;

// Progress-only layout (blocking legacy-import dialog): just the bar(s) with the percentage, with breathing room
// above and below.
const ProgressOnlyContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px 24px 24px;
`;

const ProgressOnlyRow = styled.div`
    margin: 16px 0;
`;

type LibraryImportTask = NonNullable<libraryImportTaskQueriesListQuery['response']['libraryImportTasks']>[number];

const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

const getStatusBadge = (task: LibraryImportTask) => {
    switch (task.state) {
        case STATE_WAITING:
            return <Badge variant='neutral'>{T._('Queued', 'Admin')}</Badge>;
        case STATE_PROCESSING:
            return <Badge variant='info'>{T._('Processing', 'Admin')}</Badge>;
        case STATE_COMPLETED:
            return task.errorCount > 0 ? (
                <Badge variant='warning'>{T._('Completed with errors', 'Admin')}</Badge>
            ) : (
                <Badge variant='success'>{T._('Completed', 'Admin')}</Badge>
            );
        case STATE_ERROR:
            return <Badge variant='danger'>{T._('Error', 'Admin')}</Badge>;
        default:
            return <Badge variant='neutral'>{task.stateName}</Badge>;
    }
};

const renderImagesCount = (task: LibraryImportTask) => {
    if (task.state === STATE_WAITING && !task.totalImages) {
        return <span>-</span>;
    }
    return (
        <span>
            {task.importedImages} / {task.totalImages}
        </span>
    );
};

const renderProgress = (task: LibraryImportTask) => {
    // Waiting before the worker started total count: indeterminate bar.
    if (task.state === STATE_WAITING) {
        return (
            <ProgressBarContainer>
                <ProgressBar>
                    <IndeterminateFill />
                </ProgressBar>
                <ProgressText>{T._('Queued', 'Admin')}</ProgressText>
            </ProgressBarContainer>
        );
    }

    // progressPercent is already computed server-side (0..100): use it directly, never recompute.
    const percent = task.state === STATE_COMPLETED ? 100 : task.progressPercent;

    return (
        <ProgressBarContainer title={task.state === STATE_ERROR ? task.lastError || task.displayMessage || undefined : undefined}>
            <ProgressBar>
                <ProgressFill $progress={percent} $state={task.state} />
            </ProgressBar>
            <ProgressText>{percent}%</ProgressText>
        </ProgressBarContainer>
    );
};

// Coarse status of the seller's library import tasks, used to drive the blocking onboarding overlay: `hasActive` = at
// least one task waiting/processing (must block), `hasError` = at least one task failed.
export interface LibraryImportStatus {
    hasActive: boolean;
    hasError: boolean;
}

interface LibraryImportTasksContentProps {
    // Only tasks for this import target (0=ImageGallery, 1=Background) are considered; the others are ignored so the
    // image gallery never reacts to a background import and vice versa.
    target: number;
    fetchKey: number;
    onRefetch: () => void;
    onTasksSnapshot: (activeIds: number[], completedIds: number[]) => void;
    onStatusChange?: (status: LibraryImportStatus) => void;
    onTasksProgress?: () => void;
    // Render only the progress bar(s) with their percentage — no table, no date/status/images/actions. Used by the
    // legacy-import blocking dialog.
    progressOnly: boolean;
}

const LibraryImportTasksContent: FC<LibraryImportTasksContentProps> = ({
    target,
    fetchKey,
    onRefetch,
    onTasksSnapshot,
    onStatusChange,
    onTasksProgress,
    progressOnly
}) => {
    // Poll by bumping fetchKey: it is part of Relay's query cache key, so each change forces a real network request
    // (remounting with the same variables would just reuse the cached result and the progress would stay frozen).
    // store-and-network renders the previous values from the store while refetching, so the numbers update without a
    // loading flicker on every poll.
    const data = useLazyLoadQuery<libraryImportTaskQueriesListQuery>(libraryImportTasksQuery, { state: null }, { fetchPolicy: 'store-and-network', fetchKey });

    const [restartTask] = useMutation<libraryImportTaskQueriesRestartMutation>(restartLibraryImportTaskMutation);
    const [restartingTaskId, setRestartingTaskId] = useState<number | null>(null);

    // Only the tasks for this gallery's target: the query returns every task of the seller (both targets).
    const tasks = (data.libraryImportTasks ?? []).filter((task) => task.target === target);

    // Keep the most recent first.
    const sortedTasks = [...tasks].sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

    const hasActive = tasks.some((task) => task.state === STATE_WAITING || task.state === STATE_PROCESSING);
    const hasError = tasks.some((task) => task.state === STATE_ERROR);
    // Total images imported so far across all tasks; increases as the worker progresses.
    const importedTotal = tasks.reduce((sum, task) => sum + (task.importedImages ?? 0), 0);

    // Poll only while at least one task is queued or processing, then stop. An idle gallery view must NOT keep polling:
    // with the gallery open by many sellers at once, continuous idle polling would hammer the backend for nothing. The
    // initial load already runs one query (enough to decide whether to block); a task started after load
    // (e.g. auto-assigned at signup) is picked up on the next page load.
    useEffect(() => {
        if (!hasActive) {
            return;
        }

        const intervalId = setInterval(() => {
            onRefetch();
        }, POLLING_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [hasActive, onRefetch]);

    // Report the coarse status so the parent can block/reveal the gallery. Fires only on transitions.
    useEffect(() => {
        onStatusChange?.({ hasActive, hasError });
    }, [hasActive, hasError, onStatusChange]);

    // Report every time the imported count grows so the parent can refresh the grid and reveal images as they land.
    useEffect(() => {
        onTasksProgress?.();
    }, [importedTotal, onTasksProgress]);

    // Report a snapshot of which tasks are active vs completed. The "active -> completed" comparison is done by the
    // parent wrapper, whose ref survives the per-poll remount of this component (a ref kept here would reset every poll).
    useEffect(() => {
        const activeIds = tasks.filter((task) => task.state === STATE_WAITING || task.state === STATE_PROCESSING).map((task) => task.taskID);
        const completedIds = tasks.filter((task) => task.state === STATE_COMPLETED).map((task) => task.taskID);
        onTasksSnapshot(activeIds, completedIds);
    }, [tasks, onTasksSnapshot]);

    const handleRestartTask = (taskID: number) => {
        setRestartingTaskId(taskID);
        showInfoNotification({ message: T._('Restarting task...', 'Admin') });

        restartTask({
            variables: { taskID },
            onCompleted: () => {
                setRestartingTaskId(null);
                showSuccessNotification({ message: T._('Task restarted successfully.', 'Admin') });
                // Task is back to Waiting: refresh the list to resume polling.
                onRefetch();
            },
            onError: () => {
                setRestartingTaskId(null);
                // The task may no longer be in Error state (already re-queued) or not owned by the seller: just refresh.
                showErrorNotification({ message: T._('Failed to restart task.', 'Admin') });
                onRefetch();
            }
        });
    };

    const renderActions = (task: LibraryImportTask) => {
        if (task.state !== STATE_ERROR) {
            return <span>-</span>;
        }
        return (
            <ActionButtonsContainer>
                <PrimaryButton disabled={restartingTaskId === task.taskID} onClick={() => handleRestartTask(task.taskID)}>
                    {T._('Restart', 'Admin')}
                </PrimaryButton>
            </ActionButtonsContainer>
        );
    };

    if (sortedTasks.length === 0) {
        if (progressOnly) {
            return null;
        }
        return (
            <Section>
                <EmptyList
                    title={T._('No uploads in progress', 'Admin')}
                    subTitle={T._('Library import tasks will appear here while your gallery images are being uploaded.', 'Admin')}
                />
            </Section>
        );
    }

    if (progressOnly) {
        return (
            <ProgressOnlyContainer>
                {sortedTasks.map((task) => (
                    <ProgressOnlyRow key={task.taskID}>{renderProgress(task)}</ProgressOnlyRow>
                ))}
            </ProgressOnlyContainer>
        );
    }

    const headings: TableHeading[] = [
        [T._('Date', 'Admin'), '1fr'],
        [T._('Status', 'Admin'), '0.8fr'],
        [T._('Images', 'Admin'), '0.6fr'],
        [T._('Progress', 'Admin'), '1fr'],
        [T._('Actions', 'Admin'), 'auto']
    ];

    return (
        <TableWrapper>
            <Table headings={headings}>
                {sortedTasks.map((task) => (
                    <Row
                        key={task.taskID}
                        cells={[
                            <CellContainer>{formatDate(task.createdOn)}</CellContainer>,
                            <CellContainer>{getStatusBadge(task)}</CellContainer>,
                            <CellContainer>{renderImagesCount(task)}</CellContainer>,
                            <CellContainer>{renderProgress(task)}</CellContainer>,
                            renderActions(task)
                        ]}
                    />
                ))}
            </Table>
        </TableWrapper>
    );
};

export const LibraryImportTasks: FC<{
    // Import target this instance tracks (0=ImageGallery, 1=Background). Tasks of the other target are ignored.
    target: number;
    onTasksCompleted?: () => void;
    onStatusChange?: (status: LibraryImportStatus) => void;
    onTasksProgress?: () => void;
    // Bump to force an immediate refetch (e.g. right after triggering a manual import) so a task started outside this
    // component is picked up without waiting for the next poll.
    refreshSignal?: number;
    // Render only the progress bar(s) with percentage (no table). Used by the legacy-import blocking dialog.
    progressOnly?: boolean;
}> = ({ target, onTasksCompleted, onStatusChange, onTasksProgress, refreshSignal, progressOnly = false }) => {
    const [refreshKey, setRefreshKey] = useState(0);

    const handleRefetch = useCallback(() => setRefreshKey((prev) => prev + 1), []);

    // Refetch when the external signal changes (skip the initial mount value).
    const lastRefreshSignal = useRef(refreshSignal);
    useEffect(() => {
        if (refreshSignal !== lastRefreshSignal.current) {
            lastRefreshSignal.current = refreshSignal;
            handleRefetch();
        }
    }, [refreshSignal, handleRefetch]);

    // Completion detection lives here, in this stable wrapper: its ref persists across polls and can compare the previous
    // poll's active tasks against the current completed ones, refreshing the image grid exactly once per completion.
    const previouslyActiveIds = useRef<Set<number>>(new Set());
    const handleTasksSnapshot = useCallback(
        (activeIds: number[], completedIds: number[]) => {
            const justCompleted = completedIds.some((id) => previouslyActiveIds.current.has(id));
            if (justCompleted) {
                onTasksCompleted?.();
            }
            previouslyActiveIds.current = new Set(activeIds);
        },
        [onTasksCompleted]
    );

    return (
        <Suspense fallback={<TableContentLoader />}>
            <LibraryImportTasksContent
                target={target}
                fetchKey={refreshKey}
                onRefetch={handleRefetch}
                onTasksSnapshot={handleTasksSnapshot}
                onStatusChange={onStatusChange}
                onTasksProgress={onTasksProgress}
                progressOnly={progressOnly}
            />
        </Suspense>
    );
};

export default LibraryImportTasks;
